import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  Stack,
  Divider,
  LinearProgress,
  InputAdornment,
  alpha,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  GridView,
  ViewList,
  Refresh,
  Category as CategoryIcon,
  Image as ImageIcon,
  UploadFile,
  Warning,
  Search,
  DragIndicator,
  CheckCircle,
  Cancel,
  Inventory2,
} from '@mui/icons-material';

import {
  fetchCategories,
  createCategory,
  updateCategory,
  deleteCategory,
} from '../../api/api';
import api from '../../api/api';
import { useSnackbar } from '../../utils/snackbar';

// ─── helpers ────────────────────────────────────────────────────────────────

const slugify = (str) =>
  str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const EMPTY_FORM = {
  name: '',
  slug: '',
  description: '',
  image: '',
  active: true,
  parentId: '',
};

// ─── sub-components ──────────────────────────────────────────────────────────

function CategoryImage({ src, name, size = 56 }) {
  const [err, setErr] = useState(false);
  if (!src || err) {
    return (
      <Avatar
        variant="rounded"
        sx={{
          width: size,
          height: size,
          bgcolor: 'action.selected',
          color: 'text.secondary',
        }}
      >
        <CategoryIcon sx={{ fontSize: size * 0.5 }} />
      </Avatar>
    );
  }
  return (
    <Avatar
      variant="rounded"
      src={src}
      alt={name}
      sx={{ width: size, height: size }}
      imgProps={{ onError: () => setErr(true), style: { objectFit: 'cover' } }}
    />
  );
}

function ImageUploadZone({ value, onChange, uploading }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(value || '');
  const [imgErr, setImgErr] = useState(false);

  useEffect(() => {
    setPreview(value || '');
    setImgErr(false);
  }, [value]);

  const handleFile = async (file) => {
    if (!file) return;
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
    setImgErr(false);
    onChange(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) handleFile(file);
  };

  return (
    <Box>
      <Box
        onClick={() => !uploading && inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        sx={{
          border: '2px dashed',
          borderColor: 'divider',
          borderRadius: 2,
          minHeight: 140,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: uploading ? 'default' : 'pointer',
          overflow: 'hidden',
          position: 'relative',
          transition: 'border-color 0.2s',
          bgcolor: 'background.default',
          '&:hover': { borderColor: 'primary.main' },
        }}
      >
        {uploading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (theme) => alpha(theme.palette.background.paper, 0.8),
              zIndex: 1,
              gap: 1,
            }}
          >
            <CircularProgress size={28} />
            <Typography variant="caption" color="text.secondary">
              Uploading…
            </Typography>
          </Box>
        )}

        {preview && !imgErr ? (
          <img
            src={preview}
            alt="preview"
            onError={() => setImgErr(true)}
            style={{ width: '100%', height: 140, objectFit: 'cover' }}
          />
        ) : (
          <Stack alignItems="center" spacing={1} sx={{ p: 3, color: 'text.secondary' }}>
            <UploadFile sx={{ fontSize: 36 }} />
            <Typography variant="body2">Click or drag image here</Typography>
            <Typography variant="caption">PNG, JPG, WEBP — max 5 MB</Typography>
          </Stack>
        )}
      </Box>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => handleFile(e.target.files[0])}
      />

      {preview && !imgErr && !uploading && (
        <Button
          size="small"
          color="error"
          sx={{ mt: 0.5 }}
          onClick={() => {
            setPreview('');
            onChange(null);
          }}
        >
          Remove image
        </Button>
      )}
    </Box>
  );
}

// ─── main component ──────────────────────────────────────────────────────────

export default function ManageCategories() {
  const { showAlert } = useSnackbar();

  // list state
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'

  // add/edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [pendingImageFile, setPendingImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [slugManual, setSlugManual] = useState(false);

  // delete dialog
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // ── data loading ──────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetchCategories();
      setCategories(res.data?.categories || res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load categories.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── derived data ──────────────────────────────────────────────────────────

  const filtered = categories.filter((c) =>
    !search ||
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.slug?.toLowerCase().includes(search.toLowerCase())
  );

  const activeCount = categories.filter((c) => c.active !== false).length;

  // ── dialog helpers ────────────────────────────────────────────────────────

  const openAdd = () => {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setPendingImageFile(null);
    setSlugManual(false);
    setSaveError('');
    setDialogOpen(true);
  };

  const openEdit = (cat) => {
    setEditTarget(cat);
    setForm({
      name: cat.name || '',
      slug: cat.slug || '',
      description: cat.description || '',
      image: cat.image || '',
      active: cat.active !== false,
      parentId: cat.parentId || cat.parent || '',
    });
    setPendingImageFile(null);
    setSlugManual(true); // don't auto-overwrite slug on edit
    setSaveError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saveLoading || uploading) return;
    setDialogOpen(false);
  };

  const handleNameChange = (val) => {
    setForm((f) => ({
      ...f,
      name: val,
      slug: slugManual ? f.slug : slugify(val),
    }));
  };

  const handleImageChange = (fileOrNull) => {
    if (fileOrNull === null) {
      setPendingImageFile(null);
      setForm((f) => ({ ...f, image: '' }));
    } else {
      setPendingImageFile(fileOrNull);
    }
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    setUploading(true);
    try {
      const res = await api.post('/api/upload', formData);
      return res.data?.url || '';
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setSaveError('Category name is required.');
      return;
    }
    try {
      setSaveLoading(true);
      setSaveError('');

      let imageUrl = form.image;
      if (pendingImageFile) {
        imageUrl = await uploadImage(pendingImageFile);
      }

      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim() || slugify(form.name),
        description: form.description.trim(),
        image: imageUrl,
        active: form.active,
        ...(form.parentId ? { parentId: form.parentId } : {}),
      };

      if (editTarget) {
        await updateCategory(editTarget._id, payload);
        showAlert('Category updated successfully.', 'success');
      } else {
        await createCategory(payload);
        showAlert('Category created successfully.', 'success');
      }

      setDialogOpen(false);
      load();
    } catch (err) {
      setSaveError(err.response?.data?.message || 'Failed to save category.');
    } finally {
      setSaveLoading(false);
    }
  };

  // ── delete helpers ────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      await deleteCategory(deleteTarget._id);
      showAlert('Category deleted.', 'success');
      setDeleteTarget(null);
      load();
    } catch (err) {
      showAlert(err.response?.data?.message || 'Delete failed.', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── render helpers ────────────────────────────────────────────────────────

  const parentOptions = categories.filter(
    (c) => !editTarget || c._id !== editTarget._id
  );

  // ═════════════════════════════════════════════════════════════════════════
  //  RENDER
  // ═════════════════════════════════════════════════════════════════════════

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* ── HEADER ── */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
              <CategoryIcon fontSize="small" />
            </Avatar>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h6" fontWeight={700}>
                  Categories
                </Typography>
                <Chip
                  label={`${categories.length} total`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={`${activeCount} active`}
                  size="small"
                  color="success"
                  variant="filled"
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Manage product categories, slugs, and hierarchy
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={load} disabled={loading} size="small">
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openAdd}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
            >
              Add Category
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* ── TOOLBAR ── */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          placeholder="Search categories…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flexGrow: 1, maxWidth: { sm: 320 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          size="small"
          onChange={(_, v) => v && setViewMode(v)}
        >
          <ToggleButton value="grid" aria-label="Grid view">
            <Tooltip title="Grid view">
              <GridView fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="list" aria-label="List view">
            <Tooltip title="List view">
              <ViewList fontSize="small" />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* ── LOADING BAR ── */}
      {loading && <LinearProgress sx={{ borderRadius: 1 }} />}

      {/* ── ERROR ── */}
      {error && !loading && (
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* ── DRAG HINT ── */}
      {!loading && !error && categories.length > 1 && (
        <Alert
          icon={<DragIndicator fontSize="small" />}
          severity="info"
          sx={{ py: 0.5 }}
        >
          <Typography variant="caption">
            Reorder hint: use the backend to set sort order. Drag-to-reorder coming soon.
          </Typography>
        </Alert>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          GRID VIEW
      ══════════════════════════════════════════════════════════════════ */}
      {viewMode === 'grid' && !loading && !error && (
        <>
          {filtered.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                p: 8,
                textAlign: 'center',
                bgcolor: 'background.paper',
              }}
            >
              <CategoryIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {search ? 'No categories match your search' : 'No categories yet'}
              </Typography>
              <Typography variant="body2" color="text.disabled" mb={3}>
                {search
                  ? 'Try adjusting your search term.'
                  : 'Create your first category to organise your products.'}
              </Typography>
              {!search && (
                <Button variant="contained" startIcon={<Add />} onClick={openAdd}>
                  Add Category
                </Button>
              )}
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {filtered.map((cat) => (
                <Grid item key={cat._id} xs={12} sm={6} md={4} lg={3}>
                  <Card
                    elevation={0}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'box-shadow 0.2s, border-color 0.2s',
                      '&:hover': {
                        boxShadow: 3,
                        borderColor: 'primary.light',
                      },
                    }}
                  >
                    {/* card image */}
                    <Box
                      sx={{
                        height: 140,
                        bgcolor: 'action.hover',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        borderRadius: '8px 8px 0 0',
                        position: 'relative',
                      }}
                    >
                      {cat.image ? (
                        <CardImageWithFallback src={cat.image} name={cat.name} />
                      ) : (
                        <CategoryIcon sx={{ fontSize: 52, color: 'action.disabled' }} />
                      )}

                      {/* active badge */}
                      <Chip
                        size="small"
                        label={cat.active !== false ? 'Active' : 'Inactive'}
                        color={cat.active !== false ? 'success' : 'default'}
                        icon={
                          cat.active !== false ? (
                            <CheckCircle sx={{ fontSize: '14px !important' }} />
                          ) : (
                            <Cancel sx={{ fontSize: '14px !important' }} />
                          )
                        }
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          fontWeight: 600,
                          fontSize: '0.68rem',
                        }}
                      />
                    </Box>

                    <CardContent sx={{ flexGrow: 1, pb: 0 }}>
                      <Typography variant="subtitle1" fontWeight={700} noWrap>
                        {cat.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: 'monospace' }}
                        noWrap
                        display="block"
                      >
                        /{cat.slug}
                      </Typography>

                      {cat.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 0.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {cat.description}
                        </Typography>
                      )}

                      {/* product count badge */}
                      {cat.productCount != null && (
                        <Stack direction="row" alignItems="center" spacing={0.5} mt={1}>
                          <Inventory2 sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {cat.productCount} product{cat.productCount !== 1 ? 's' : ''}
                          </Typography>
                        </Stack>
                      )}
                    </CardContent>

                    <CardActions sx={{ px: 1.5, pb: 1.5, pt: 1, justifyContent: 'flex-end' }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(cat)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteTarget(cat)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          LIST VIEW
      ══════════════════════════════════════════════════════════════════ */}
      {viewMode === 'list' && !loading && !error && (
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 700, width: 64 }}>Image</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Slug</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Products</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <CategoryIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1, display: 'block', mx: 'auto' }} />
                      <Typography color="text.secondary">
                        {search ? 'No categories match your search.' : 'No categories found.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((cat) => (
                    <TableRow
                      key={cat._id}
                      hover
                      sx={{ '&:last-child td': { border: 0 } }}
                    >
                      <TableCell>
                        <CategoryImage src={cat.image} name={cat.name} size={44} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {cat.name}
                        </Typography>
                        {cat.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            noWrap
                            sx={{ maxWidth: 240 }}
                          >
                            {cat.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                        >
                          /{cat.slug}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {cat.productCount != null ? (
                          <Chip
                            label={cat.productCount}
                            size="small"
                            icon={<Inventory2 sx={{ fontSize: '14px !important' }} />}
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={cat.active !== false ? 'Active' : 'Inactive'}
                          color={cat.active !== false ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(cat)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteTarget(cat)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          ADD / EDIT DIALOG
      ══════════════════════════════════════════════════════════════════ */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, bgcolor: 'background.paper' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 0 }}>
          {editTarget ? 'Edit Category' : 'Add Category'}
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2 }}>
          <Stack spacing={2.5}>
            {saveError && <Alert severity="error">{saveError}</Alert>}

            {/* name */}
            <TextField
              label="Category Name"
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              fullWidth
              required
              autoFocus
              size="small"
              inputProps={{ maxLength: 80 }}
            />

            {/* slug */}
            <TextField
              label="Slug"
              value={form.slug}
              onChange={(e) => {
                setSlugManual(true);
                setForm((f) => ({ ...f, slug: e.target.value }));
              }}
              fullWidth
              size="small"
              helperText="URL-friendly identifier — auto-generated from name"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography variant="caption" color="text.secondary">/</Typography>
                  </InputAdornment>
                ),
              }}
            />

            {/* description */}
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              size="small"
              inputProps={{ maxLength: 500 }}
            />

            {/* image upload */}
            <Box>
              <Typography variant="body2" fontWeight={600} mb={0.75}>
                Category Image
              </Typography>
              <ImageUploadZone
                value={form.image}
                onChange={handleImageChange}
                uploading={uploading}
              />
            </Box>

            <Divider />

            {/* parent category */}
            <FormControl fullWidth size="small">
              <InputLabel>Parent Category (optional)</InputLabel>
              <Select
                value={form.parentId}
                label="Parent Category (optional)"
                onChange={(e) => setForm((f) => ({ ...f, parentId: e.target.value }))}
              >
                <MenuItem value="">
                  <em>None — top-level category</em>
                </MenuItem>
                {parentOptions.map((c) => (
                  <MenuItem key={c._id} value={c._id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* active toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                  color="success"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {form.active ? 'Active' : 'Inactive'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {form.active
                      ? 'Category is visible to customers'
                      : 'Category is hidden from the storefront'}
                  </Typography>
                </Box>
              }
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeDialog} disabled={saveLoading || uploading} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saveLoading || uploading || !form.name.trim()}
            startIcon={saveLoading || uploading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 600, minWidth: 110 }}
          >
            {saveLoading ? 'Saving…' : uploading ? 'Uploading…' : editTarget ? 'Save Changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════
          DELETE CONFIRMATION DIALOG
      ══════════════════════════════════════════════════════════════════ */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleteLoading && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, bgcolor: 'background.paper' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="error" />
          Delete Category
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2}>
            <Typography>
              Are you sure you want to delete{' '}
              <strong>{deleteTarget?.name}</strong>?
            </Typography>

            <Alert severity="warning" icon={<Warning fontSize="small" />}>
              Products currently assigned to this category may become uncategorised.
              This action cannot be undone.
            </Alert>

            {deleteTarget?.productCount > 0 && (
              <Alert severity="error">
                This category has <strong>{deleteTarget.productCount}</strong> product
                {deleteTarget.productCount !== 1 ? 's' : ''} attached. Consider
                reassigning them before deleting.
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleteLoading}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} color="inherit" /> : <Delete />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {deleteLoading ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── card image with fallback (defined outside to avoid re-mount on grid re-render) ──

function CardImageWithFallback({ src, name }) {
  const [err, setErr] = useState(false);
  if (err) {
    return <CategoryIcon sx={{ fontSize: 52, color: 'action.disabled' }} />;
  }
  return (
    <img
      src={src}
      alt={name}
      onError={() => setErr(true)}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );
}
